// Pure helpers for a scaling card's "Max weekly budget to test" field: the
// period converter, the highest-observed-week anchor, and the spend checks.
// Everything here is weekly; see the scaling design doc, decision 4.4.
import { DataQaGroup, DataQaResults, DataQaSeries, DataQaSeriesGroup } from '../../dataQaContract';
import {
  BudgetPeriodType,
  Card,
  SCALING_PREFILL_MULTIPLE,
  SCALING_WARN_MULTIPLE,
  WEEKS_PER_PERIOD
} from './request-form-constants';
import { comboKey, isPlatformPooled, isTacticPooled } from './request-form-utils';

const FULL_WEEK_DAYS = 7;

/** A total budget over `period`, as $ per week. */
export const totalToWeekly = (total: number, period: BudgetPeriodType): number =>
  total / WEEKS_PER_PERIOD[period];

/** $ per week, as a total budget over `period`. */
export const weeklyToTotal = (weekly: number, period: BudgetPeriodType): number =>
  weekly * WEEKS_PER_PERIOD[period];

/** Whole dollars with thousands separators, e.g. `$74,492`. */
export const formatDollars = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

/** An ISO week start as `Dec 1, 2025`, read as UTC so it never shifts a day. */
export const formatWeekOf = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });

export interface HighestWeek {
  spend: number;
  /** ISO Monday of that week. */
  weekStart: string;
}

type CardSelection = Pick<Card, 'channel' | 'platform' | 'tactic' | 'kpi' | 'grain'>;

/** The QA groups a card's grain sums over: its own row at finest, every row
 *  sharing the retained dimensions at a pooled grain. */
export const groupsForCard = (groups: DataQaGroup[], card: CardSelection): DataQaGroup[] =>
  groups.filter(
    g =>
      g.channel === card.channel &&
      g.kpi === card.kpi &&
      (isPlatformPooled(card.grain) || g.platform === card.platform) &&
      (isTacticPooled(card.grain) || g.tactic === card.tactic)
  );

/**
 * Highest weekly spend across the given finest-grain series, summed week by
 * week. A week counts once at least one contributing group observed all 7
 * days, which drops the partial weeks at the window's edges. It can't require
 * every group to be complete: "observed" means the feed sent a row, so a
 * paused or late-launched group would knock out most weeks of a pool. A group
 * missing days just adds the spend it had, as econ's raw weekly sum does.
 */
export const highestFullWeek = (series: DataQaSeriesGroup[]): HighestWeek | null => {
  const weeks = new Map<string, { spend: number; full: boolean }>();
  series.forEach(sg =>
    sg.week_start.forEach((week, i) => {
      const prev = weeks.get(week) ?? { spend: 0, full: false };
      const spend = sg.spend[i];
      weeks.set(week, {
        spend: prev.spend + (Number.isFinite(spend) ? spend : 0),
        full: prev.full || (sg.n_days_observed[i] ?? 0) >= FULL_WEEK_DAYS
      });
    })
  );
  let best: HighestWeek | null = null;
  [...weeks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([weekStart, { spend, full }]) => {
      // Strictly greater, over ascending weeks: a tie keeps the earliest week.
      if (full && spend > 0 && (!best || spend > best.spend)) {
        best = { spend, weekStart };
      }
    });
  return best;
};

/** The card's highest full week at its grain, or null without series data. */
export const highestWeekForCard = (
  qa: DataQaResults,
  series: DataQaSeries | undefined,
  card: CardSelection
): HighestWeek | null => {
  if (!series) {
    return null;
  }
  const seriesGroups = groupsForCard(qa.groups, card)
    .map(g => series.groups[g.id])
    .filter((sg): sg is DataQaSeriesGroup => Boolean(sg));
  return highestFullWeek(seriesGroups);
};

/** The default max weekly spend: 2x the highest week, in whole dollars. */
export const prefillMaxWeeklySpend = (highest: HighestWeek): string =>
  String(Math.round(highest.spend * SCALING_PREFILL_MULTIPLE));

export type SpendCheck = { level: 'error' | 'warning'; message: string } | null;

/** Blocks submit at or below the highest week; warns past 3x it. Without a
 *  known highest week, only a positive amount is required. */
export const checkMaxWeeklySpend = (value: string, highestSpend?: number): SpendCheck => {
  const weekly = Number(value);
  if (value.trim() === '' || !Number.isFinite(weekly) || weekly <= 0) {
    return { level: 'error', message: 'Enter a weekly spend to test up to.' };
  }
  if (highestSpend === undefined || !(highestSpend > 0)) {
    return null;
  }
  if (weekly <= highestSpend) {
    return {
      level: 'error',
      message: `Enter more than your highest week (${formatDollars(highestSpend)}). The curve already covers spend up to there.`
    };
  }
  if (weekly > highestSpend * SCALING_WARN_MULTIPLE) {
    return {
      level: 'warning',
      message: `That's ${(weekly / highestSpend).toFixed(1)}x your highest week. The far end of the curve is extrapolated well past your data, so read it with care.`
    };
  }
  return null;
};

/** Why the Scaling side of a card's selector is disabled (fails at every grain). */
export const scalingUnavailableReason = (
  qa: DataQaResults,
  card: Pick<Card, 'channel' | 'platform' | 'tactic' | 'kpi'>
): string => {
  const minWeeks = qa.thresholds?.scaling_min_weeks ?? 26;
  const key = comboKey(card.channel, card.platform, card.tactic, card.kpi);
  const group = qa.groups.find(g => comboKey(g.channel, g.platform, g.tactic, g.kpi) === key);
  // Prefer the count the gate uses (weeks with spend AND results, econ #99);
  // older QA runs only carry full weeks of spend.
  const scalingWeeks = group?.detail?.sample_size?.n_scaling_weeks;
  if (scalingWeeks) {
    const weeks = Math.max(scalingWeeks.volume ?? 0, scalingWeeks.revenue ?? 0);
    return `Scaling unavailable: scaling needs ${minWeeks} full weeks with both spend and results at some grain. This combo has ${weeks}.`;
  }
  const weeks = group?.detail?.sample_size?.n_valid_weeks?.spend;
  const has = typeof weeks === 'number' ? ` This combo has ${weeks}.` : '';
  return `Scaling unavailable: scaling needs ${minWeeks} full weeks of spend at some grain.${has}`;
};
